import classNames from 'classnames';
import React, { useState } from 'react';
import { useFragment, useQuery } from '@apollo/client';
import PageLayout from '../app/layout/PageLayout';
import { useSidebarOpenState } from '../app/layout/SidebarOpenStateContext';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Button, { IconButton } from '../components/Button';
import HotkeyInfo from '../components/HotkeyInfo';
import SVGIcon from '../components/SVGIcon';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated/gql';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import sidebarToggleIcon from '../icons/non-prefilled/16x16/sidebarToggle.svg';
import topicIcon from '../icons/prefilled/16x16/topic.svg';
import { getPostIcon } from '../post/PostIcon';
import SidebarRow, { SidebarRowRightContent } from '../sidebar/SidebarRow';
import SidebarSection from '../sidebar/SidebarSection';
import SidebarSectionAnimation from '../sidebar/SidebarSectionAnimation';
import { getSidebarSectionNameWithFallback } from '../sidebar/SidebarSectionsProvider';
import { UIActions } from '../ui/uiSlice';
import { publicPostPath } from '../utils/url/postPath';
import { publicTopicPath } from '../utils/url/topicPath';
import useCanScrollUp from '../utils/useCanScrollUp';
import useShortcut from '../utils/useShortcut';
import PublicSearchModal from './PublicSearchModal';
import * as styles from './PublicSidebar.module.scss';
import usePublicAncestors from './usePublicAncestors';

interface SidebarToggleButtonProps {
  floating?: boolean;
}

const SidebarToggleButton = ({ floating }: SidebarToggleButtonProps) => {
  const { toggleSidebar } = useSidebarOpenState();

  return (
    <div
      className={classNames(styles.sidebarToggleButtonContainer, {
        [styles.isFloating]: floating,
      })}
    >
      <IconButton
        onClick={() => {
          toggleSidebar();
        }}
        aria-label="Toggle toolbar"
        className={styles.sidebarToggleButton}
        size="xl"
      >
        {sidebarToggleIcon}
      </IconButton>
    </div>
  );
};

const POST_FRAGMENT = gql(`
  fragment PublicSidebarPost on PublicPost {
    id
    title
    ...getPostIcon_post
    ...publicPostPath_post
  }
`);

const TOPIC_FRAGMENT = gql(`
  fragment PublicSidebarTopic on PublicTopic {
    id
    name
    posts {
      id
      ...PublicSidebarPost
    }
    children {
      id
    }
  }
`);

const TOPIC_QUERY = gql(/** @complexity 5 */ `
  query PublicSidebarTopicQuery($id: ID!) {
    publicTopic(id: $id) {
      id
      ...PublicSidebarTopic
    }
  }
`);

const SECTION_QUERY = gql(/** @complexity 118 */ `
  query PublicSidebarSectionQuery {
    sections: publicSections {
      id
      name
      items {
        ... on PublicPost {
          id
          ...PublicSidebarPost
        }
        ... on PublicTopic {
          id
          ...PublicSidebarTopic
          children {
            ...PublicSidebarTopic
            children {
              ...PublicSidebarTopic
            }
          }
        }
      }
    }
  }
`);

interface PublicSidebarPostProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  level: number;
}

const PublicSidebarPost = ({ post, level }: PublicSidebarPostProps) => {
  return (
    <SidebarRow
      key={post.id}
      icon={getPostIcon(post, true)}
      to={publicPostPath(post)}
      level={level}
    >
      {post.title}
    </SidebarRow>
  );
};

interface PublicSidebarTopicProps {
  expandState: Map<string, boolean>;
  setExpandState: (state: Map<string, boolean>) => void;
  topicId: string;
  level: number;
}

const PublicSidebarTopic = ({
  topicId,
  level,
  expandState,
  setExpandState,
}: PublicSidebarTopicProps) => {
  // Few first topic levels are fetched in the initial query, so we load
  // them from cache and only fetch deeper levels with a separate query
  const { data: fragmentData, complete: isComplete } = useFragment({
    fragment: TOPIC_FRAGMENT,
    fragmentName: 'PublicSidebarTopic',
    from: { __typename: 'PublicTopic', id: topicId },
  });

  const { data: queryData } = useQuery(TOPIC_QUERY, {
    variables: { id: topicId },
    fetchPolicy: 'cache-first',
    skip: !!fragmentData && isComplete,
  });

  const topic =
    fragmentData && isComplete ? fragmentData : queryData?.publicTopic;

  if (!topic) {
    return null;
  }

  const isExpanded = expandState.get(topicId);
  const children = (
    <>
      {topic.posts.map((post) => (
        <PublicSidebarPost key={post.id} post={post} level={level + 1} />
      ))}
      {topic.children.map((subtopic) => (
        <PublicSidebarTopic
          key={subtopic.id}
          topicId={subtopic.id}
          level={level + 1}
          expandState={expandState}
          setExpandState={setExpandState}
        />
      ))}
    </>
  );

  return (
    <>
      <SidebarRow
        icon={topicIcon}
        to={publicTopicPath(topic)}
        level={level}
        rightContent={
          <SidebarRowRightContent
            expandState={isExpanded ? 'expanded' : 'collapsed'}
            onExpandClick={() =>
              setExpandState(new Map(expandState).set(topicId, !isExpanded))
            }
          />
        }
      >
        {topic.name}
      </SidebarRow>
      <SidebarSectionAnimation sectionKey={topicId} expanded={!!isExpanded}>
        {!!topic.children?.length || !!topic.posts.length ? children : null}
      </SidebarSectionAnimation>
    </>
  );
};

const PublicSidebar = () => {
  const { isSidebarOpen } = useSidebarOpenState();

  const { data } = useQuery(SECTION_QUERY);
  const publicSections = data?.sections;
  const dispatch = useAppDispatch();

  const [expandState, setExpandState] = useState(new Map<string, boolean>());
  const ancestors = usePublicAncestors();

  if (ancestors.values().some((key) => !expandState.has(key))) {
    const newState = new Map(expandState);
    for (const ancestor of ancestors) {
      if (!newState.has(ancestor)) {
        newState.set(ancestor, true);
      }
    }
    setExpandState(newState);
  }

  const openSearchModal = () => {
    dispatch(UIActions.openModal(PublicSearchModal, { alignToTop: true }));
  };

  useShortcut('/', (e) => {
    e.preventDefault();
    openSearchModal();
  });

  const { setScrollingContainer, canScrollUp } = useCanScrollUp();

  return (
    <>
      <PageLayout.Sidebar
        publicVariant
        className={classNames(styles.container, {
          [styles.isScrolled]: canScrollUp,
        })}
      >
        <div className={styles.searchBarContainer}>
          <Button
            icon={<SVGIcon className={styles.searchIcon}>{searchIcon}</SVGIcon>}
            className={styles.searchBar}
            onClick={() => {
              openSearchModal();
            }}
          >
            Search
          </Button>
          <div className={styles.shortcut}>
            <HotkeyInfo keys={new Set(['/'])} />
          </div>
        </div>
        {publicSections && (
          <div ref={setScrollingContainer} className={styles.sections}>
            {publicSections.map((section) => {
              return (
                <SidebarSection
                  publicVariant
                  key={section.id}
                  id={section.id}
                  title={getSidebarSectionNameWithFallback(section.name)}
                  hasChildren={section.items.length > 0}
                >
                  {section.items.map((item) => {
                    if (item.__typename === 'PublicTopic') {
                      return (
                        <PublicSidebarTopic
                          key={item.id}
                          topicId={item.id}
                          level={0}
                          expandState={expandState}
                          setExpandState={setExpandState}
                        />
                      );
                    }
                    if (item.__typename === 'PublicPost') {
                      return (
                        <PublicSidebarPost
                          key={item.id}
                          post={item}
                          level={0}
                        />
                      );
                    }
                    return null;
                  })}
                </SidebarSection>
              );
            })}
          </div>
        )}
        <SidebarToggleButton />
      </PageLayout.Sidebar>
      {!isSidebarOpen && <SidebarToggleButton floating />}
    </>
  );
};

export default PublicSidebar;
